import React from "react"

import { Target, TrendingDown, TrendingUp } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { type OverallMetrics, type ViewMode } from "@/lib/weekdayComparison"

import { getLoadFactorColor } from "./loadFactorColors"

interface OverallMetricsCardsProps {
  metrics: OverallMetrics
  dayCount: number
  viewMode: ViewMode
}

const OverallMetricsCards: React.FC<OverallMetricsCardsProps> = ({
  metrics,
  dayCount,
  viewMode,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-palette-jungleGreen500">
          Average Load Factor
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "text-3xl font-bold",
            getLoadFactorColor(metrics.avgLoadFactor),
          )}
        >
          {metrics.avgLoadFactor.toFixed(1)}%
        </div>
        <div className="text-xs text-palette-jungleGreen500 mt-1">
          Across {dayCount} {viewMode === "weekday" ? "dates" : "days"}
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-palette-jungleGreen500">
          Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          {metrics.trendDirection === "increasing" ? (
            <TrendingUp className="h-5 w-5 text-red-600" />
          ) : metrics.trendDirection === "decreasing" ? (
            <TrendingDown className="h-5 w-5 text-green-600" />
          ) : (
            <Target className="h-5 w-5 text-palette-jungleGreen600" />
          )}
          <div
            className={cn(
              "text-2xl font-semibold",
              metrics.trendDirection === "increasing"
                ? "text-red-600"
                : metrics.trendDirection === "decreasing"
                  ? "text-green-600"
                  : "text-palette-jungleGreen600",
            )}
          >
            {metrics.trend > 0 ? "+" : ""}
            {metrics.trend.toFixed(2)}% / week
          </div>
        </div>
        <div className="text-xs text-palette-jungleGreen500 mt-1 capitalize">
          {metrics.trendDirection}
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-palette-jungleGreen500">
          Total Routes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-palette-jungleGreen900">
          {metrics.totalRoutes}
        </div>
        <div className="text-xs text-palette-jungleGreen500 mt-1">Included in analysis</div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-palette-jungleGreen500">
          Wasted Capacity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-amber-600">
          {metrics.totalWastedCapacity.toFixed(1)}
        </div>
        <div className="text-xs text-palette-jungleGreen500 mt-1">UK pallets unused</div>
      </CardContent>
    </Card>
  </div>
)

export default OverallMetricsCards
